import { useMemo } from 'react';
import useCheckoutState, { checkoutStates } from './useCheckoutState';
import { PAGE_TYPES } from '../enums/page-types';
import hasStock from '../lib/hasStock';
import getSelectedTicketTypes from '../lib/getSelectedTicketTypes';
import useSelectedPrice from './useSelectedPrice';

const useSubmitDisabled = (paymentFormLoading = false) => {
  const {
    state,
    checkout,
    product,
    form,
    isUpsell,
    upsell,
    isTesting,
    isTestingPayments,
    isPreview,
  } = useCheckoutState();

  const selectedPrice = useSelectedPrice();
  const enabledPriceCount = product?.prices?.filter(p => p.enabled !== false).length ?? 0;
  const noPriceSelected = enabledPriceCount > 1 && !selectedPrice;

  // The event twin of noPriceSelected: add-ons alone are not a booking, and the
  // server refuses a charge with no ticket, so the button waits for one.
  const noTicketSelected =
    checkout.type === PAGE_TYPES.EVENT &&
    getSelectedTicketTypes(checkout.ticketGroups, form?.ticketGroups).length === 0;

  const submitIsDisabled = useMemo(() => {
    const alreadySubmitting =
      state === checkoutStates.CREATING_PAYMENT ||
      state === checkoutStates.CREATING_SUBSCRIPTION ||
      state === checkoutStates.CREATING_SUBMISSION;

    const productIsSoldOut = checkout.type === PAGE_TYPES.CHECKOUT && !hasStock(product);
    const eventIsSoldOut = checkout.type === PAGE_TYPES.EVENT && checkout.eventDetails?.isSoldOut;
    const eventIsCanceled =
      checkout.type === PAGE_TYPES.EVENT && checkout.eventDetails?.status === 'canceled';

    const isTestingUpsellWithoutPayment = isTesting && isUpsell && !upsell;
    const isTestingOutsideTestPayments = isTesting && !isTestingPayments && isPreview;
    const isTestingScenario = isTestingUpsellWithoutPayment || isTestingOutsideTestPayments;

    if (
      alreadySubmitting ||
      noPriceSelected ||
      productIsSoldOut ||
      eventIsSoldOut ||
      eventIsCanceled ||
      noTicketSelected ||
      isTestingScenario ||
      paymentFormLoading
    ) {
      return true;
    }

    return false;
  }, [
    state,
    checkout.type,
    checkout.eventDetails?.isSoldOut,
    product,
    isUpsell,
    upsell,
    isTesting,
    isTestingPayments,
    isPreview,
    noPriceSelected,
    noTicketSelected,
    paymentFormLoading,
  ]);

  const getDisabledReason = () => {
    const alreadySubmitting =
      state === checkoutStates.CREATING_PAYMENT ||
      state === checkoutStates.CREATING_SUBSCRIPTION ||
      state === checkoutStates.CREATING_SUBMISSION;

    if (alreadySubmitting) return 'submitting';

    if (noPriceSelected) return 'no_price_selected';

    const productIsSoldOut = checkout.type === PAGE_TYPES.CHECKOUT && !hasStock(product);
    if (productIsSoldOut) return 'product_sold_out';

    const eventIsSoldOut = checkout.type === PAGE_TYPES.EVENT && checkout.eventDetails?.isSoldOut;
    if (eventIsSoldOut) return 'event_sold_out';

    if (noTicketSelected) return 'no_ticket_selected';

    const isTestingUpsellWithoutPayment = isTesting && isUpsell && !upsell;
    if (isTestingUpsellWithoutPayment) return 'testing_upsell_without_payment';

    const isTestingOutsideTestPayments = isTesting && !isTestingPayments && isPreview;
    if (isTestingOutsideTestPayments) return 'testing_outside_test_payments';

    if (paymentFormLoading) return 'payment_form_loading';

    return null;
  };

  return {
    submitIsDisabled,
    disabledReason: submitIsDisabled ? getDisabledReason() : null,
  };
};

export default useSubmitDisabled;
